import { useState, useMemo, useCallback } from "react";
import {
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Minus,
  Plus,
  GripVertical,
} from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { apiFetch } from "../lib/api";

/* ── Types ── */

interface FolderBase {
  id: string;
  name: string;
  sort_order?: number;
  parent_id?: string | null;
  folder_type?: string;
}

interface LibraryFolder extends FolderBase {
  item_count: number;
}

interface ChatEpisode {
  id: string;
  title: string;
  podcast_name?: string;
}

interface ChatFolder extends FolderBase {
  episode_count: number;
  episodes?: ChatEpisode[];
}

interface FlatNode<T extends FolderBase> {
  folder: T;
  depth: number;
  hasChildren?: boolean;
}

/* ── Tree utilities ── */

function buildAndFlatten<T extends FolderBase>(folders: T[]): { nodes: FlatNode<T>[]; childrenMap: Map<string, string[]> } {
  const sorted = [...folders].sort((a, b) => {
    const oa = a.sort_order ?? 999999;
    const ob = b.sort_order ?? 999999;
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name);
  });

  const ids = new Set(sorted.map((f) => f.id));
  const childrenMap = new Map<string, T[]>();
  const childIdsMap = new Map<string, string[]>();
  const roots: T[] = [];

  for (const f of sorted) {
    if (f.parent_id && ids.has(f.parent_id)) {
      const arr = childrenMap.get(f.parent_id) || [];
      arr.push(f);
      childrenMap.set(f.parent_id, arr);
      
      const idArr = childIdsMap.get(f.parent_id) || [];
      idArr.push(f.id);
      childIdsMap.set(f.parent_id, idArr);
    } else {
      roots.push(f);
    }
  }

  const result: FlatNode<T>[] = [];
  function dfs(node: T, depth: number) {
    const hasChildren = childrenMap.has(node.id);
    result.push({ folder: node, depth, hasChildren });
    for (const child of childrenMap.get(node.id) || []) dfs(child, depth + 1);
  }
  for (const root of roots) dfs(root, 0);
  return { nodes: result, childrenMap: childIdsMap };
}

/* ── Sortable library row ── */

function SortableLibraryRow({
  node,
  isSelected,
  onSelect,
  onDelete,
  isSubfolderExpanded,
  onToggleSubfolders,
}: {
  node: FlatNode<LibraryFolder>;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  isSubfolderExpanded?: boolean;
  onToggleSubfolders?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: node.folder.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginLeft: `${node.depth * 20}px`,
      }}
      className={node.depth > 0 ? "border-l-2 border-gray-200 pl-2" : ""}
    >
      <div className="group flex items-center">
        {/* Drag handle - hidden for now, keep code for future re-enable */}
        <div
          className="hidden p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
        {/* Subfolder expand toggle */}
        {node.hasChildren && onToggleSubfolders && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSubfolders();
            }}
            className="p-0.5 text-gray-400 hover:text-gray-600 shrink-0"
          >
            {isSubfolderExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        )}
        {/* Spacer when no children */}
        {!node.hasChildren && <div className="w-4" />}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className={`flex-1 min-h-[44px] rounded-md px-3 py-2 text-left text-sm flex items-center gap-1.5 transition-colors min-w-0 touch-manipulation ${
            isSelected ? "bg-blue-100 text-blue-700 font-medium" : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
          }`}
        >
          <span className="flex-1 truncate">{node.folder.name}</span>
          <span className="text-xs text-gray-400 shrink-0">{node.folder.item_count}</span>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity shrink-0"
          aria-label={`Delete folder ${node.folder.name}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ── Sortable chat row ── */

function SortableChatRow({
  node,
  isSelected,
  isPartial,
  isExpanded,
  isLoadingEpisodes,
  episodes,
  selectedItems,
  onToggle,
  onExpand,
  onToggleEpisode,
  isSubfolderExpanded: _isSubfolderExpanded,
  onToggleSubfolders,
}: {
  node: FlatNode<ChatFolder>;
  isSelected: boolean;
  isPartial: boolean;
  isExpanded: boolean;
  isLoadingEpisodes: boolean;
  episodes?: ChatEpisode[];
  selectedItems: Set<string>;
  onToggle: () => void;
  onExpand: () => void;
  onToggleEpisode: (episodeId: string) => void;
  isSubfolderExpanded?: boolean;
  onToggleSubfolders?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: node.folder.id });

  const isCustomFolder = node.folder.folder_type === "custom";

  // Group episodes by channel for custom folders
  const groupedEpisodes = useMemo(() => {
    if (!isCustomFolder || !episodes || episodes.length === 0) return null;
    const groups = new Map<string, ChatEpisode[]>();
    for (const ep of episodes) {
      const key = ep.podcast_name || "Uncategorized";
      const arr = groups.get(key) || [];
      arr.push(ep);
      groups.set(key, arr);
    }
    if (groups.size <= 1) return null;
    return groups;
  }, [isCustomFolder, episodes]);

  const renderEpisode = (ep: ChatEpisode) => (
    <button
      key={ep.id}
      onClick={(e) => {
        e.stopPropagation();
        onToggleEpisode(ep.id);
      }}
      className={`w-full min-h-[44px] rounded px-3 py-2 text-left text-sm flex items-center gap-1.5 transition-colors touch-manipulation ${
        selectedItems.has(ep.id) ? "bg-blue-100 text-blue-700 font-medium" : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
      }`}
    >
      <span className="flex-1 truncate">{ep.title}</span>
      {selectedItems.has(ep.id) && <Check className="w-3 h-3 shrink-0" />}
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginLeft: `${node.depth * 20}px`,
      }}
      className={node.depth > 0 ? "border-l-2 border-gray-200 pl-2" : ""}
    >
      <div className="flex items-center gap-0.5">
        {/* Drag handle - hidden for now, keep code for future re-enable */}
        <div
          className="hidden p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
        {/* Selection checkbox - LEFT of name */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="p-2 shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
          title={isSelected ? "Deselect folder" : "Select folder"}
        >
          <div
            className={`w-4 h-4 rounded border-[1.5px] flex items-center justify-center transition-colors ${
              isSelected
                ? "bg-blue-600 border-blue-600"
                : isPartial
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
            }`}
          >
            {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            {isPartial && !isSelected && <div className="w-2 h-0.5 bg-blue-400 rounded" />}
          </div>
        </button>
        {/* Name area — click to select/deselect folder */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={`flex-1 min-h-[44px] rounded-md px-3 py-2 text-left text-sm flex items-center gap-1.5 transition-colors min-w-0 touch-manipulation ${
            isSelected
              ? "bg-blue-50 text-blue-700 font-medium"
              : isPartial
                ? "bg-blue-50/50 text-blue-600"
                : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
          }`}
        >
          <span className="flex-1 truncate">{node.folder.name}</span>
          <span className="text-xs text-gray-400 shrink-0">{node.folder.episode_count}</span>
        </button>
        {/* Expand toggle - RIGHT side, + becomes - when expanded */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            // If folder has children, toggle subfolder expansion
            if (node.hasChildren && onToggleSubfolders) {
              onToggleSubfolders();
            } else {
              onExpand();
            }
          }}
          className="p-2 shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 touch-manipulation"
          title={isExpanded ? "Collapse episodes" : "Expand episodes"}
        >
          {isExpanded ? (
            <Minus className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
        </button>
      </div>
      {isExpanded && (
        <div className="mt-0.5 space-y-0.5" style={{ marginLeft: `${24}px` }}>
          {isLoadingEpisodes ? (
            <p className="px-2 py-1 text-sm text-gray-400">Loading...</p>
          ) : episodes?.length === 0 ? (
            <p className="px-2 py-1 text-sm text-gray-400">No episodes</p>
          ) : groupedEpisodes ? (
            Array.from(groupedEpisodes).map(([channel, eps]) => (
              <div key={channel} className="mb-1">
                <p className="px-2 py-0.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{channel}</p>
                {eps.map(renderEpisode)}
              </div>
            ))
          ) : (
            episodes?.map(renderEpisode)
          )}
        </div>
      )}
    </div>
  );
}

/* ── Drag overlays ── */

function LibraryOverlay({ node }: { node: FlatNode<LibraryFolder> }) {
  return (
    <div
      className="flex min-h-[36px] items-center gap-1.5 rounded-md bg-white px-2 py-1 text-sm shadow-lg ring-2 ring-blue-400"
      style={{ paddingLeft: `${node.depth * 16}px` }}
    >
      <ChevronRight className="h-3.5 w-3.5 text-gray-300 shrink-0" />
      <span className="truncate font-medium">{node.folder.name}</span>
    </div>
  );
}

function ChatOverlay({ node }: { node: FlatNode<ChatFolder> }) {
  return (
    <div className="flex min-h-[36px] items-center gap-1.5 rounded-md bg-white px-2 py-1 text-sm shadow-lg ring-2 ring-blue-400">
      <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
      <span className="truncate font-medium">{node.folder.name}</span>
    </div>
  );
}

/* ── Props ── */

interface BaseProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

interface LibraryModeProps extends BaseProps {
  mode: "library";
  folders: LibraryFolder[];
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onDeleteFolder: (id: string) => void;
  onFoldersReordered: () => void;
  onCreateFolder: (name: string) => Promise<void>;
}

interface ChatModeProps extends BaseProps {
  mode: "chat";
  folders: ChatFolder[];
  selectedFolders: Set<string>;
  selectedItems: Set<string>;
  expandedFolders: Set<string>;
  loadingEpisodes: Set<string>;
  loadingFolders: boolean;
  onToggleFolder: (folderId: string) => void;
  onToggleEpisode: (folderId: string, episodeId: string) => void;
  onExpandFolder: (folderId: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onFoldersReordered?: () => void;
  onCreateFolder?: (name: string) => Promise<void>;
}

type Props = LibraryModeProps | ChatModeProps;

/* ── Main component ── */

export default function SharedFolderSidebar(props: Props) {
  const { mode, collapsed, onToggleCollapse } = props;

  const [customCollapsed, setCustomCollapsed] = useState(false);
  const [subscribedCollapsed, setSubscribedCollapsed] = useState(false);
  const [channelsCollapsed, setChannelsCollapsed] = useState(true);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  
  // Track which parent folders have their subfolders expanded
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Split folders into sections
  const allFolders = props.folders as FolderBase[];
  const customFolders = useMemo(
    () => allFolders.filter((f) => f.folder_type === "custom"),
    [allFolders]
  );
  const subscribedFolders = useMemo(
    () => allFolders.filter((f) => f.folder_type === "subscribed"),
    [allFolders]
  );
  const channelFolders = useMemo(
    () => allFolders.filter((f) => f.folder_type === "channel" || f.folder_type === "auto" || !f.folder_type),
    [allFolders]
  );

  const sectionCount = [customFolders.length, subscribedFolders.length, channelFolders.length].filter((n) => n > 0).length;
  // Always show sections in library mode, or when there are multiple non-empty sections
  const hasMultipleSections = mode === "library" || sectionCount > 1;

  // Build trees with children info
  const { nodes: customNodes } = useMemo(() => buildAndFlatten(customFolders), [customFolders]);
  const { nodes: subscribedNodes } = useMemo(() => buildAndFlatten(subscribedFolders), [subscribedFolders]);
  const { nodes: channelNodes } = useMemo(() => buildAndFlatten(channelFolders), [channelFolders]);
  const { nodes: singleNodes } = useMemo(
    () => (hasMultipleSections ? { nodes: [], childrenMap: new Map() } : buildAndFlatten(allFolders)),
    [hasMultipleSections, allFolders]
  );

  // Toggle subfolder expansion
  const toggleParentExpanded = useCallback((folderId: string) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  // Filter nodes to hide children of collapsed parents
  const filterVisibleNodes = useCallback(<T extends FolderBase>(nodes: FlatNode<T>[]): FlatNode<T>[] => {
    const visible: FlatNode<T>[] = [];
    const hiddenParents = new Set<string>();
    
    for (const node of nodes) {
      // If this node's parent is hidden, skip it
      if (node.folder.parent_id && hiddenParents.has(node.folder.parent_id)) {
        hiddenParents.add(node.folder.id); // Also hide its children
        continue;
      }
      
      // If this node is a parent and NOT expanded, mark for hiding children
      if (node.hasChildren && !expandedParents.has(node.folder.id)) {
        hiddenParents.add(node.folder.id);
      }
      
      visible.push(node);
    }
    
    return visible;
  }, [expandedParents]);

  const visibleCustomNodes = useMemo(() => filterVisibleNodes(customNodes), [filterVisibleNodes, customNodes]);
  const visibleSubscribedNodes = useMemo(() => filterVisibleNodes(subscribedNodes), [filterVisibleNodes, subscribedNodes]);
  const visibleChannelNodes = useMemo(() => filterVisibleNodes(channelNodes), [filterVisibleNodes, channelNodes]);
  const visibleSingleNodes = useMemo(() => filterVisibleNodes(singleNodes), [filterVisibleNodes, singleNodes]);

  const customIds = useMemo(() => visibleCustomNodes.map((n) => n.folder.id), [visibleCustomNodes]);
  const subscribedIds = useMemo(() => visibleSubscribedNodes.map((n) => n.folder.id), [visibleSubscribedNodes]);
  const channelIds = useMemo(() => visibleChannelNodes.map((n) => n.folder.id), [visibleChannelNodes]);
  const singleIds = useMemo(() => visibleSingleNodes.map((n) => n.folder.id), [visibleSingleNodes]);

  const onFoldersReordered =
    mode === "library" ? props.onFoldersReordered : (props as ChatModeProps).onFoldersReordered;

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveId(e.active.id as string);
  }, []);

  const makeDragEnd = useCallback(
    (sectionIds: string[], beforeIds: string[], afterIds: string[]) =>
      async (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over || active.id === over.id) return;

        const oldIdx = sectionIds.indexOf(active.id as string);
        const newIdx = sectionIds.indexOf(over.id as string);
        if (oldIdx === -1 || newIdx === -1) return;

        const reordered = [...sectionIds];
        reordered.splice(oldIdx, 1);
        reordered.splice(newIdx, 0, active.id as string);

        const fullOrder = [...beforeIds, ...reordered, ...afterIds];

        try {
          await apiFetch("/folders/reorder", {
            method: "POST",
            body: JSON.stringify({ folder_ids: fullOrder }),
          });
          onFoldersReordered?.();
        } catch (err) {
          console.error("Failed to reorder folders:", err);
          onFoldersReordered?.();
        }
      },
    [onFoldersReordered]
  );

  const handleSingleDragEnd = useMemo(() => makeDragEnd(singleIds, [], []), [makeDragEnd, singleIds]);
  const handleCustomDragEnd = useMemo(
    () => makeDragEnd(customIds, [], [...subscribedIds, ...channelIds]),
    [makeDragEnd, customIds, subscribedIds, channelIds]
  );
  const handleSubscribedDragEnd = useMemo(
    () => makeDragEnd(subscribedIds, customIds, channelIds),
    [makeDragEnd, subscribedIds, customIds, channelIds]
  );
  const handleChannelDragEnd = useMemo(
    () => makeDragEnd(channelIds, [...customIds, ...subscribedIds], []),
    [makeDragEnd, channelIds, customIds, subscribedIds]
  );

  const canCreateFolder = mode === "library" || !!(props as ChatModeProps).onCreateFolder;

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    if (mode === "library") {
      await props.onCreateFolder(newFolderName.trim());
    } else if ((props as ChatModeProps).onCreateFolder) {
      await (props as ChatModeProps).onCreateFolder!(newFolderName.trim());
    }
    setCreatingFolder(false);
    setNewFolderName("");
  };

  // Active node for DragOverlay
  const allNodes = hasMultipleSections
    ? [...visibleCustomNodes, ...visibleSubscribedNodes, ...visibleChannelNodes]
    : visibleSingleNodes;
  const activeNode = activeId ? allNodes.find((n) => n.folder.id === activeId) : null;

  // ── Render helpers ──

  const renderLibraryItems = (nodes: FlatNode<FolderBase>[]) =>
    nodes.length === 0 ? (
      <p className="px-3 py-4 text-center text-sm text-gray-400">No folders yet</p>
    ) : (
      <div className="space-y-0.5">
        {nodes.map((node) => (
          <SortableLibraryRow
            key={node.folder.id}
            node={node as FlatNode<LibraryFolder>}
            isSelected={(props as LibraryModeProps).selectedFolderId === node.folder.id}
            onSelect={() => (props as LibraryModeProps).onSelectFolder(node.folder.id)}
            onDelete={() => (props as LibraryModeProps).onDeleteFolder(node.folder.id)}
            isSubfolderExpanded={expandedParents.has(node.folder.id)}
            onToggleSubfolders={node.hasChildren ? () => toggleParentExpanded(node.folder.id) : undefined}
          />
        ))}
      </div>
    );

  const renderChatItems = (nodes: FlatNode<FolderBase>[]) => {
    const cp = props as ChatModeProps;
    if (cp.loadingFolders) return <p className="px-2 py-2 text-sm text-gray-400">Loading...</p>;
    if (nodes.length === 0)
      return <p className="px-2 py-2 text-sm text-gray-400">No folders yet. Transcribe content first!</p>;
    return (
      <div className="space-y-0.5">
        {nodes.map((node) => {
          const f = node.folder as ChatFolder;
          return (
            <SortableChatRow
              key={f.id}
              node={node as FlatNode<ChatFolder>}
              isSelected={cp.selectedFolders.has(f.id)}
              isPartial={!cp.selectedFolders.has(f.id) && !!f.episodes?.some((ep) => cp.selectedItems.has(ep.id))}
              isExpanded={cp.expandedFolders.has(f.id)}
              isLoadingEpisodes={cp.loadingEpisodes.has(f.id)}
              episodes={f.episodes}
              selectedItems={cp.selectedItems}
              onToggle={() => cp.onToggleFolder(f.id)}
              onExpand={() => cp.onExpandFolder(f.id)}
              onToggleEpisode={(epId) => cp.onToggleEpisode(f.id, epId)}
              isSubfolderExpanded={expandedParents.has(f.id)}
              onToggleSubfolders={node.hasChildren ? () => toggleParentExpanded(f.id) : undefined}
            />
          );
        })}
      </div>
    );
  };

  const renderItems = (nodes: FlatNode<FolderBase>[]) =>
    mode === "library" ? renderLibraryItems(nodes) : renderChatItems(nodes);

  const renderOverlay = () =>
    activeNode ? (
      mode === "library" ? (
        <LibraryOverlay node={activeNode as FlatNode<LibraryFolder>} />
      ) : (
        <ChatOverlay node={activeNode as FlatNode<ChatFolder>} />
      )
    ) : null;

  const renderCreationForm = () =>
    creatingFolder ? (
      <div className="mb-2 flex items-center gap-1 px-1">
        <input
          autoFocus
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreateFolder();
            if (e.key === "Escape") {
              setCreatingFolder(false);
              setNewFolderName("");
            }
          }}
          placeholder="Folder name"
          className="min-w-0 flex-1 rounded-md border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button onClick={handleCreateFolder} className="shrink-0 text-lg leading-none text-green-600 hover:text-green-500" aria-label="Create folder">
          &#10003;
        </button>
        <button
          onClick={() => {
            setCreatingFolder(false);
            setNewFolderName("");
          }}
          className="shrink-0 text-lg leading-none text-gray-400 hover:text-gray-600"
          aria-label="Cancel"
        >
          &#10005;
        </button>
      </div>
    ) : null;

  const renderSectionHeader = (label: string, count: number, isCollapsed: boolean, onToggle: () => void, showCreate = false) => (
    <div className="mb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-gray-400">
      <button
        onClick={onToggle}
        className="flex items-center gap-1 hover:text-gray-600 touch-manipulation min-h-[32px]"
      >
        <ChevronRight className={`h-3 w-3 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
        {label} ({count})
      </button>
      {showCreate && canCreateFolder && (
        <button
          onClick={() => setCreatingFolder(true)}
          className="text-blue-500 hover:text-blue-600 normal-case font-medium touch-manipulation min-h-[32px] px-2"
        >
          + New
        </button>
      )}
    </div>
  );

  // ── Layout ──

  if (collapsed) {
    return (
      <div className="w-full flex flex-col items-start py-2">
        <button 
          onClick={onToggleCollapse}
          onTouchEnd={(e) => {
            e.preventDefault();
            onToggleCollapse();
          }}
          className="min-h-[44px] min-w-[44px] p-3 text-gray-500 hover:text-gray-700 active:bg-gray-200 rounded-md touch-manipulation flex items-center gap-2"
          aria-label="Expand folders"
        >
          <ChevronRight className="h-5 w-5" />
          <span className="text-sm font-medium">Folders</span>
        </button>
      </div>
    );
  }

  // Helper to render a section
  const renderSection = (
    label: string,
    nodes: FlatNode<FolderBase>[],
    ids: string[],
    isCollapsed: boolean,
    onToggle: () => void,
    onDragEnd: (event: DragEndEvent) => Promise<void>,
    showCreate = false
  ) => (
    <div key={label}>
      {renderSectionHeader(label, nodes.length, isCollapsed, onToggle, showCreate)}
      {!isCollapsed && (
        <div className={label === "Channels" ? "max-h-[60vh] overflow-y-auto overscroll-contain pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent [-webkit-overflow-scrolling:touch] md:max-h-[calc(100vh-18rem)]" : undefined}>
          {showCreate && renderCreationForm()}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={onDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {renderItems(nodes)}
            </SortableContext>
            <DragOverlay>{renderOverlay()}</DragOverlay>
          </DndContext>
        </div>
      )}
    </div>
  );

  // ── Chat mode: select all / none ──
  const renderSelectionControls = () => {
    if (mode !== "chat") return null;
    const cp = props as ChatModeProps;
    return (
      <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
        <button onClick={cp.onSelectAll} className="text-blue-600 hover:underline touch-manipulation min-h-[32px] px-1">
          All
        </button>
        <span>|</span>
        <button onClick={cp.onSelectNone} className="text-blue-600 hover:underline touch-manipulation min-h-[32px] px-1">
          None
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-y-auto overflow-x-hidden px-2 py-2">
      {renderSelectionControls()}

      {hasMultipleSections ? (
        <div className="space-y-4">
          {visibleCustomNodes.length > 0 &&
            renderSection("Custom", visibleCustomNodes, customIds, customCollapsed, () => setCustomCollapsed((v) => !v), handleCustomDragEnd, true)}
          {visibleSubscribedNodes.length > 0 &&
            renderSection("Favorites", visibleSubscribedNodes, subscribedIds, subscribedCollapsed, () => setSubscribedCollapsed((v) => !v), handleSubscribedDragEnd)}
          {visibleChannelNodes.length > 0 &&
            renderSection("Channels", visibleChannelNodes, channelIds, channelsCollapsed, () => setChannelsCollapsed((v) => !v), handleChannelDragEnd)}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleSingleDragEnd}>
          <SortableContext items={singleIds} strategy={verticalListSortingStrategy}>
            {renderItems(visibleSingleNodes)}
          </SortableContext>
          <DragOverlay>{renderOverlay()}</DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
